import { createReducer, Action, on } from '@ngrx/store';

import * as ManureSpaceActions from './manure-space.actions';
import {
  createLoadedResource,
  createErrorResource,
  createLoadingResource,
  createUninitializedResource,
  ExternalResource,
} from '../../shared/models/resource.models';

import {
  InitialManureSpaceState,
  ManureSpace,
  ManureSpaceState,
} from '../models/manure-space.models';

const reducer = createReducer<ManureSpaceState>(
  {
    ...InitialManureSpaceState,
  },

  on(ManureSpaceActions.setManureSpaceSuccess, (state, { manureSpace }) => ({
    ...state,
    manureSpace: manureSpace
      ? createLoadedResource<ManureSpace>(manureSpace)
      : createLoadedResource<ManureSpace>(null as unknown as ManureSpace),
  })),

  on(ManureSpaceActions.setManureSpaceFailure, (state, { error }) => ({
    ...state,
    manureSpace: createErrorResource(error as Error),
  })),

  on(ManureSpaceActions.setManureSpaceFilteredByManureCodeSuccess, (state, { manureSpace }) => ({
    ...state,
    manureSpaceFilteredByManureCode: manureSpace
      ? createLoadedResource<ManureSpace>(manureSpace)
      : createLoadedResource<ManureSpace>(null as unknown as ManureSpace),
  })),

  on(ManureSpaceActions.setManureSpaceFilteredByManureCodeFailure, (state, { error }) => ({
    ...state,
    manureSpaceFilteredByManureCode: createErrorResource(error as Error),
  })),

  on(ManureSpaceActions.setFertilizersFromDelivery, (state, { fertilizers }) => ({
    ...state,
    manureSpace: {
      ...state.manureSpace,
    },
    fertilizersFromDelivery: fertilizers,
  })),

  on(ManureSpaceActions.setAdvisedFertilizers, (state, { fertilizers }) => ({
    ...state,
    manureSpace: {
      ...state.manureSpace,
    },
    fertilizersFromDelivery: fertilizers,
  })),

  on(ManureSpaceActions.getManureSpaceByPeriod, (state) => ({
    ...state,
    manureSpaceByPeriod: createLoadingResource(),
  })),

  on(ManureSpaceActions.setManureSpaceByPeriodSuccess, (state, { manureSpace }) => ({
    ...state,
    manureSpaceByPeriod: manureSpace
      ? createLoadedResource<ManureSpace>(manureSpace)
      : createLoadedResource<ManureSpace>(null as unknown as ManureSpace),
  })),

  on(ManureSpaceActions.setManureSpaceByPeriodFailure, (state, { error }) => ({
    ...state,
    manureSpaceByPeriod: createErrorResource(error as Error),
  })),

  on(ManureSpaceActions.resetManureData, (state) => ({
    ...state,
    manureSpace: createUninitializedResource(),
    manureSpaceFilteredByManureCode: createUninitializedResource(),
    manureSpaceByPeriod: createUninitializedResource(),
  })),

  on(ManureSpaceActions.setActiveTab, (state, { tabIndex }) => ({
    ...state,
    activeTabIndex: tabIndex,
  })),

  on(ManureSpaceActions.setManureSpaceComment, (state, { comment }) => {
    return {
      ...state,
      manureSpace: updateCommentIfLoaded(state.manureSpace, comment),
      manureSpaceFilteredByManureCode: updateCommentIfLoaded(
        state.manureSpaceFilteredByManureCode,
        comment,
      ),
      manureSpaceByPeriod: updateCommentIfLoaded(state.manureSpaceByPeriod, comment),
    };
  }),

  on(ManureSpaceActions.setInitialStockMatchesRvo, (state, { initialStockMatchesRvo }) => ({
    ...state,
    manureSpace: updateInitialStockMatchesRvoIfLoaded(state.manureSpace, initialStockMatchesRvo),
    manureSpaceFilteredByManureCode: updateInitialStockMatchesRvoIfLoaded(
      state.manureSpaceFilteredByManureCode,
      initialStockMatchesRvo,
    ),
    manureSpaceByPeriod: updateInitialStockMatchesRvoIfLoaded(
      state.manureSpaceByPeriod,
      initialStockMatchesRvo,
    ),
  })),
);

const updateCommentIfLoaded = <T extends { comment?: string }>(
  resource: ExternalResource<T>,
  comment: string,
): ExternalResource<T> => {
  if (resource?.state === 'loaded' && resource.data) {
    return createLoadedResource({
      ...resource.data,
      comment,
    });
  }
  return resource;
};

const updateInitialStockMatchesRvoIfLoaded = <T extends { initialStockMatchesRvo?: boolean }>(
  resource: ExternalResource<T>,
  initialStockMatchesRvo: boolean,
): ExternalResource<T> => {
  if (resource?.state === 'loaded' && resource.data) {
    return createLoadedResource({
      ...resource.data,
      initialStockMatchesRvo,
    });
  }
  return resource;
};

export const manureSpaceReducer = (state: ManureSpaceState | undefined, action: Action) =>
  reducer(state, action);
